import Vue from 'vue'
import { indexByAsync } from '../utils/algorithms'

const timeIntervalInMilliseconds = 10 * 60 * 1000 // 10min

function canFetchNewData(state, timeIntervalToFetch) {
  if (!state?.data) return true
  if (!state?.lastSyncDate) return true
  if (Date.now() - state.lastSyncDate >= timeIntervalToFetch) return true
  return false
}

function hasPreconditionsToFetch(rootState, state) {
  if (
    rootState.auth?.user?.employeeCode === undefined ||
    rootState.auth.user.employeeCode === null
  )
    return false
  if (state.loading) return false
  return true
}

export const state = () => ({
  data: null,
  loading: false,
  lastSyncDate: null,
  indexes: {},
  indicators: {},
})

async function indexIndicatorsAsync(array) {
  const map = {}
  let idx = 0
  const releaseControlAmount = 100
  if (!Array.isArray(array)) return map
  for (const client of array) {
    idx++
    if (idx % releaseControlAmount === 0)
      await new Promise(resolve => setTimeout(resolve, 0))
    if (client.indicators) {
      map[client.code] = client
    }
  }
  return map
}

export const getters = {
  indexes: state => state.indexes,
  indicators: state => state.indicators,
}

export const mutations = {
  UPDATE_ROOT: (state, payload) => Object.assign(state, payload),
  updateClient: (state, payload) => {
    const { code, comercialDivision, ...clientPayload } = payload
    const clientIdx = state.data.findIndex(
      c =>
        c.code === code &&
        (c.comercialDivision ? c.comercialDivision === comercialDivision : true)
    )
    if (clientIdx < 0) return
    const client = state.data[clientIdx]
    const newClient = {
      ...clientPayload,
      ...(clientPayload.phone !== client.phone
        ? { updates: { phone: new Date() } }
        : {}),
      ...(clientPayload.phone2 !== client.phone2
        ? { updates: { phone2: new Date() } }
        : {}),
    }
    const update = Object.assign({}, client, newClient)
    Vue.set(state.data, clientIdx, update)
  },
  verifyClientPhone: (state, payload) => {
    const { code, comercialDivision, phone, phone2 } = payload
    const clientIdx = state.data.findIndex(
      c =>
        c.code === code &&
        (c.comercialDivision ? c.comercialDivision === comercialDivision : true)
    )
    if (clientIdx < 0) return
    const client = state.data[clientIdx]
    const newClient = {
      ...client,
      phone,
      phone2,
      updates: {
        ...client.updates,
        phone: client.phone !== phone ? new Date() : client.updates.phone,
        phone2: client.phone2 !== phone2 ? new Date() : client.updates.phone2,
      },
    }
    const update = Object.assign({}, client, newClient)
    Vue.set(state.data, clientIdx, update)
    payload.isSamePhone = phone === client.phone
    payload.isSamePhone2 = phone2 === client.phone2
  },
}

export const actions = {
  async fetchPortfolio(
    { state, rootState, commit, dispatch },
    { forceUpdate } = { forceUpdate: false }
  ) {
    if (!hasPreconditionsToFetch(rootState, state)) return
    if (!canFetchNewData(state, timeIntervalInMilliseconds) && !forceUpdate) {
      if (!Object.keys(state.indexes).length) {
        dispatch('updateIndexes', state.data)
      }
      if (!Object.keys(state.indicators).length)
        indexIndicatorsAsync(state.data).then(indicators =>
          commit('UPDATE_ROOT', { indicators })
        )
      return
    }

    commit('UPDATE_ROOT', { loading: true })

    try {
      const savedState = (await this.$localforage.getItem('portfolio'))
        ?.portfolio
      if (
        (!forceUpdate &&
          savedState &&
          Date.now() - savedState.lastSyncDate < timeIntervalInMilliseconds) ||
        !window.navigator.onLine
      ) {
        dispatch('updateIndexes', savedState.data)
        indexIndicatorsAsync(savedState.data).then(indicators =>
          commit('UPDATE_ROOT', { indicators })
        )
        commit('UPDATE_ROOT', {
          ...savedState,
          loading: false,
        })
        return
      }
    } catch (error) {
      console.error('Erro getting porttfolio from localforage', error)
    }

    try {
      const data = await this.$services.portfolio.getPortfolio()
      dispatch('updateIndexes', data)
      indexIndicatorsAsync(data).then(indicators =>
        commit('UPDATE_ROOT', { indicators })
      )
      commit('UPDATE_ROOT', {
        data,
        lastSyncDate: Date.now(),
      })
    } catch (error) {
      console.error('Error fetching user portfolio', error)
      commit('UPDATE_ROOT', { lastSyncDate: null })
      window.$nuxt.$notify.error({
        message: 'Erro ao buscar carteira de clientes',
      })
    } finally {
      commit('UPDATE_ROOT', { loading: false })
    }
  },
  async updateClient({ commit, dispatch, state }, payload) {
    commit('updateClient', payload)
    await dispatch('updateIndexes', state.data)
  },
  async verifyClientPhone({ commit, dispatch, state }, payload) {
    // Usado pois commit não possue valor de retorno e já faz muita computação
    const { code, comercialDivision, phone, phone2 } = payload
    const clientIdx = state.data.findIndex(
      c =>
        c.code === code &&
        (c.comercialDivision ? c.comercialDivision === comercialDivision : true)
    )
    if (clientIdx < 0) return
    const client = state.data[clientIdx]
    const isSamePhone = client.phone === phone
    const isSamePhone2 = client.phone2 === phone2
    console.log({ payload, state })
    const trackablePayload = {
      ...payload,
      isSamePhone,
      isSamePhone2
    }
    commit('verifyClientPhone', trackablePayload)
    if (trackablePayload.isSamePhone && trackablePayload.isSamePhone2) return
    await dispatch('updateIndexes', state.data)
  },
  async updateIndexes({ commit }, data) {
    const indexes = await indexByAsync(data, [
      {
        key: 'weekday',
        // 7 represents clients with no weekday
        formatKeyValue: v => (isNaN(v) || v === null ? 7 : Number(v)),
      },
      {
        key: 'salesman.code',
        formatKey: 'salesman',
        formatKeyValue: v => Number(v),
      },
      { key: 'phone', formatKeyValue: v => !!v },
      { key: 'phone2', formatKeyValue: v => !!v },
      {
        key: 'financialStatus',
        formatKeyValue: v => {
          if (['active', 'inactive', 'blocked', 'defaulter'].includes(v))
            return v
          return 'other'
        },
      },
    ])
    commit('UPDATE_ROOT', { indexes })
  },
}
